import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Briefcase,
  Building2,
  BookOpen,
  User,
  LogIn,
  X,
  ChevronRight,
  FileText,
  Phone,
  Info,
  Star,
  GraduationCap,
  LogOut,
  Shield,
  Settings,
  LayoutDashboard,
  BookMarked,
  Bell,
  ChevronDown,
  Download,
} from 'lucide-react';
import { useConfirm } from '@/context/ConfirmContext';

const NAV_ITEMS = [
  { key: 'home',      label: 'Home',      icon: Home,      path: '/' },
  { key: 'jobs',      label: 'Jobs',      icon: Briefcase, path: '/jobs' },
];

// ─── More-menu items shown in the slide-up drawer ───────────────────────────
const MORE_ITEMS = [
  { label: 'About Us',       icon: Info,          path: '/about' },
  { label: 'Contact',        icon: Phone,         path: '/contact' },
  { label: 'Training',       icon: GraduationCap, path: '/training' },
  { label: 'Resume Builder', icon: FileText,       path: '/resume-builder' },
  { label: 'Saved Jobs',     icon: Star,           path: '/jobs?saved=true' },
  { label: 'Blogs',          icon: BookMarked,     path: '/blogs' },
];

// ─── Profile menu items (shown when user is logged in) ──────────────────────
const buildProfileItems = (userRole: string, userName: string) => {
  const items: { label: string; icon: any; path: string; danger?: boolean }[] = [];

  if (userRole === 'super_admin' || userRole === 'admin') {
    items.push({ label: 'Admin Dashboard', icon: Shield, path: '/super-admin/dashboard' });
  } else if (userRole === 'employer') {
    items.push({ label: 'Employer Dashboard', icon: LayoutDashboard, path: '/employer/dashboard' });
  } else {
    items.push({ label: 'My Dashboard', icon: LayoutDashboard, path: '/jobseeker-dashboard' });
    items.push({ label: 'Applications', icon: Briefcase, path: '/jobseeker-dashboard/applications' });
    items.push({ label: 'Saved Jobs', icon: Star, path: '/jobseeker-dashboard/saved' });
    items.push({ label: 'Resume', icon: FileText, path: '/jobseeker-dashboard/resume' });
    items.push({ label: 'Alerts', icon: Bell, path: '/jobseeker-dashboard/alerts' });
    items.push({ label: 'Settings', icon: Settings, path: '/jobseeker-dashboard/settings' });
  }

  return items;
};

// ─── Shared Drawer component ─────────────────────────────────────────────────
interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose, children, title }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        {/* Backdrop */}
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] md:hidden"
        />
        {/* Sheet sliding from bottom */}
        <motion.div
          key="drawer"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          className="fixed bottom-0 left-0 right-0 z-[201] md:hidden bg-white rounded-t-3xl shadow-2xl overflow-hidden"
          style={{ maxHeight: '80vh' }}
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-base font-bold text-gray-900">{title}</span>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}

          {/* Body */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 80px)' }}>
            {children}
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// ─── Main MobileBottomNav component ─────────────────────────────────────────
export const MobileBottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { confirm } = useConfirm();

  const [profileOpen, setProfileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    }
  };

  // Determine active tab
  const getActive = () => {
    const p = location.pathname;
    if (p === '/') return 'home';
    if (p.startsWith('/jobs')) return 'jobs';
    if (p.startsWith('/employers') || p.startsWith('/companies')) return 'employers';
    if (p.startsWith('/blogs')) return 'blogs';
    return '';
  };
  const active = getActive();

  // Resolve user info
  const userRole = String((user as any)?.user_type || (user as any)?.role || '').toLowerCase();
  const userName = (user as any)?.full_name || (user as any)?.name || (user as any)?.email || 'User';
  const initials = userName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  const profileItems = buildProfileItems(userRole, userName);

  const handleNavClick = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    setProfileOpen(false);
    const confirmed = await confirm({
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      confirmText: 'Logout',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await logout();
      navigate('/', { replace: true });
    } catch {
      navigate('/', { replace: true });
    }
  };

  // Don't show on dashboard / admin routes
  const hiddenRoutes = ['/super-admin', '/employer/dashboard', '/jobseeker-dashboard', '/jobseeker/dashboard'];
  if (hiddenRoutes.some(r => location.pathname.startsWith(r))) return null;

  return (
    <>
      {/* ── Bottom Nav Bar ── */}
      <nav
        className="
          fixed bottom-0 left-0 right-0 z-[150] md:hidden
          bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.10)]
        "
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center px-2 h-[62px]">

          {/* ── LEFT: User / Auth button ── */}
          <div className="flex-shrink-0 mr-2">
            {isAuthenticated && user ? (
              /* Avatar button → open profile drawer */
              <button
                onClick={() => { setMoreOpen(false); setProfileOpen(true); }}
                className="
                  relative flex items-center justify-center
                  w-11 h-11 rounded-full
                  bg-gradient-to-br from-[#0f76bb] to-[#0d64a0]
                  text-white font-bold text-sm
                  shadow-md active:scale-95 transition-transform
                "
                aria-label="Open profile"
              >
                {initials}
                {/* green online dot */}
                <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 bg-green-400 border-2 border-white rounded-full" />
              </button>
            ) : (
              /* Login button */
              <button
                onClick={() => navigate('/auth')}
                className="
                  flex flex-col items-center justify-center
                  w-[52px] gap-0.5
                  active:scale-95 transition-transform
                "
              >
                <div className="
                  w-9 h-9 rounded-full flex items-center justify-center
                  bg-gradient-to-br from-[#e37300] to-[#c66500]
                  shadow
                ">
                  <LogIn className="w-4 h-4 text-white" />
                </div>
                <span className="text-[9px] font-semibold text-[#e37300] leading-none">Sign In</span>
              </button>
            )}
          </div>

          {/* ── Divider ── */}
          <div className="w-px h-8 bg-gray-200 mr-2 flex-shrink-0" />

          {/* ── RIGHT: Navigation items ── */}
          <div className="flex flex-1 items-center justify-around">
            {NAV_ITEMS.map(({ key, label, icon: Icon, path }) => {
              const isActive = active === key;
              return (
                <button
                  key={key}
                  onClick={() => { setMoreOpen(false); setProfileOpen(false); handleNavClick(path); }}
                  className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 active:scale-95 transition-transform"
                  aria-label={label}
                >
                  <div className={`
                    relative flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200
                    ${isActive
                      ? 'bg-[#0f76bb] shadow-md shadow-blue-200'
                      : 'bg-transparent'
                    }
                  `}>
                    <Icon className={`w-5 h-5 transition-colors ${isActive ? 'text-white' : 'text-gray-500'}`} />
                    {isActive && (
                      <motion.div
                        layoutId="mobileNavIndicator"
                        className="absolute inset-0 rounded-xl bg-[#0f76bb]"
                        style={{ zIndex: -1 }}
                        transition={{ type: 'spring', bounce: 0.3, duration: 0.4 }}
                      />
                    )}
                  </div>
                  <span className={`text-[9px] font-semibold leading-none transition-colors ${isActive ? 'text-[#0f76bb]' : 'text-gray-500'}`}>
                    {label}
                  </span>
                </button>
              );
            })}

            {/* PWA Install App Button */}
            {deferredPrompt && (
              <button
                onClick={handleInstallClick}
                className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 active:scale-95 transition-transform"
                aria-label="Install App"
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 bg-transparent">
                  <Download className="w-5 h-5 text-gray-500" />
                </div>
                <span className="text-[9px] font-semibold leading-none text-gray-500">
                  Install App
                </span>
              </button>
            )}

            {/* More button */}
            <button
              onClick={() => { setProfileOpen(false); setMoreOpen(v => !v); }}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 active:scale-95 transition-transform"
              aria-label="More"
            >
              <div className={`
                flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200
                ${moreOpen ? 'bg-[#e37300] shadow-md' : 'bg-transparent'}
              `}>
                <ChevronDown className={`w-5 h-5 transition-all duration-200 ${moreOpen ? 'text-white rotate-180' : 'text-gray-500'}`} />
              </div>
              <span className={`text-[9px] font-semibold leading-none transition-colors ${moreOpen ? 'text-[#e37300]' : 'text-gray-500'}`}>
                More
              </span>
            </button>
          </div>
        </div>
      </nav>

      {/* ── Profile / User Drawer ── */}
      <Drawer isOpen={profileOpen} onClose={() => setProfileOpen(false)} title="">
        {/* User Card header */}
        <div className="px-5 py-4 bg-gradient-to-br from-[#0f76bb] to-[#0d64a0] text-white">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-base leading-tight truncate">{userName}</p>
              <p className="text-blue-100 text-xs capitalize mt-0.5">{userRole.replace('_', ' ') || 'Member'}</p>
            </div>
          </div>
        </div>

        {/* Menu items */}
        <div className="py-2 px-2">
          {profileItems.map((item) => (
            <button
              key={item.path}
              onClick={() => { setProfileOpen(false); navigate(item.path); }}
              className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-[#0f76bb]" />
              </div>
              <span className="text-sm font-semibold text-gray-800 flex-1">{item.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>

        {/* Logout */}
        <div className="px-4 pb-6 pt-1">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl bg-red-50 text-red-600 font-bold text-sm active:bg-red-100 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </Drawer>

      {/* ── More Drawer ── */}
      <Drawer isOpen={moreOpen} onClose={() => setMoreOpen(false)} title="More Options">
        <div className="py-2 px-2">
          {MORE_ITEMS.map((item) => (
            <button
              key={item.path}
              onClick={() => { setMoreOpen(false); navigate(item.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-full bg-[#0f76bb]/10 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-[#0f76bb]" />
              </div>
              <span className="text-sm font-semibold text-gray-800 flex-1">{item.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>
        <div className="h-4" />
      </Drawer>

      {/* ── Bottom padding spacer (so content isn't hidden behind nav) ── */}
      <div className="h-[62px] md:hidden" />
    </>
  );
};
